import { action, runInAction, observable } from 'mobx';
import { PatientDetailEditForm } from 'application/import/forms';
import {BaseEditViewStore, BaasicDropdownStore} from 'core/stores';
import {applicationContext, toDate} from 'core/utils';
import {InsuranceService, PatientDetailService, DiagnosisCodeService, DoctorService} from 'application/import/services';
import moment from 'moment';
import _ from 'lodash';

@applicationContext
class ImportPatientCreateViewStore extends BaseEditViewStore {
    @observable accessor oldPrimaryInsuranceDisplayed = false;         

    constructor(rootStore) {
        const insuranceService = new InsuranceService(rootStore.application.baasic.apiClient);
        const patientDetailService = new PatientDetailService(rootStore.application.baasic.apiClient);
        const diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        const doctorService = new DoctorService(rootStore.application.baasic.apiClient);

        const id = rootStore.routerStore.routerState.params.id;

        super(rootStore, {
            name: 'import patient',
            Form: PatientDetailEditForm,
            id: id,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await patientDetailService.get(id, {embed: 'PatientDetailDiagnosisCodes'});
                        _.extend(response.data,
                            {
                                doc: response.data.orderingDoctorId,
                                item: _.map(response.data.patientDetailDiagnosisCodes, (item => {
                                    return _.extend(
                                        item, {
                                            code: item.diagnosisCode.code                      
                                        }
                                    );
                                }))                         
                            });
                        return response.data;
                    },
                    update: async (resource) => {
                        resource.patientDetailDiagnosisCodes = _.map(resource.patientDetailDiagnosisCodes, item => {
                            return _.extend(item, {patientDetailId: id});
                        });
                        resource.id = this.item.id;
                        resource.importHeaderId = this.item.importHeaderId;
                        resource.patientGroupId = this.item.patientGroupId;
                        const response = await patientDetailService.update(resource);
                        return response;
                    }
                }
            },
            FormClass: PatientDetailEditForm,
        });

        this.diagnosisCodeService = diagnosisCodeService;
        this.patientDetailService = patientDetailService;
        this.doctorService = doctorService;
        this.insuranceService = insuranceService;

        this.contactRelationshipStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{id: 1, name: 'None'}, {id: 2, name: 'Child'}, {id: 3, name: 'Friend'}, {id: 4, name: 'Grandparent'}, {id: 5, name: 'Other'}, {id: 6, name: 'Parent'}, {id: 7, name: 'Self'}, {id: 8, name: 'Spouse'}];
            }
        });

        this.genderStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{id: 1, name: 'Male'}, {id: 2, name: 'Female'}];
            }
        });

        this.stateStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'abrv',
            filterable: true
        },{
            fetchFunc: async (term) => {
                const response = await this.patientDetailService.findStates(term);
                return response.item;
            },
        });

        this.primaryInsuranceStore = this.constructInsuranceDropdownStore('primary');
        this.oldPrimaryInsuranceStore = this.constructInsuranceDropdownStore('oldPrimary');
        this.secondaryInsuranceStore = this.constructInsuranceDropdownStore('secondary');
        this.diagnosisCodesStore = this.constructDiagnosisCodesDropdownsStore();
        this.doctorStore = this.constructDoctorDropdownsStore();
    }

    @action.bound
    async invalidateInsurances() {
        this.form.setFieldsDisabled(true);
        try {
            await this.insuranceService.invalidate();  
            this.rootStore.notificationStore.success("Insurance list successfully refreshed.");                       
        }
        catch(err) {
            this.rootStore.notificationStore.error("Error occured on while trying to refresh Insurance list.");
        }

        this.form.setFieldsDisabled(false);   
    }

    @action.bound
    constructDoctorDropdownsStore() {
        return new BaasicDropdownStore({
            textField: 'orderingDoctor',
            dataItemKey: 'orderingDoctorId',
                filterable: true,
                returnObject: true     
            }, {
            fetchFunc: async (term) => {
                const response = await this.doctorService.find({ searchQuery: term });

                _.extend(response.data,
                    {
                        item: _.map(response.data.item, (item => {
                            return _.extend(
                                item, {
                                    orderingDoctor: item.name,
                                    orderingDoctorId: item.id         
                                }
                            );
                        }))                         
                    });
                return response.data.item; 
            },
            onChange: (doctor) => {
                this.form.set({orderingDoctor: doctor.orderingDoctor, orderingDoctorId: doctor.orderingDoctorId});
            }
        });
    }

    @action.bound
    toggleOldPrimaryInsurance() {
        if(this.oldPrimaryInsuranceDisplayed){
            this.form.$('oldPrimaryInsuranceName').value = null;
            this.form.$('oldPrimaryInsurancePolicyNumber').value = null;            
            this.form.$('oldPrimaryInsuranceStartDate').value = null;
            this.form.$('oldPrimaryInsuranceEndDate').value = null;

            this.form.$('oldPrimaryInsurancePolicyNumber').set('rules', null);
            this.form.$('oldPrimaryInsuranceStartDate').set('rules', null);
            this.form.$('oldPrimaryInsuranceEndDate').set('rules', null);            
        
            this.oldPrimaryInsuranceDisplayed = false;            
        } else {
            this.oldPrimaryInsuranceDisplayed = true;
        }
    }

    @action.bound
    async goToImport() {
        if (!this.form.$('ssn').value) {
            this.rootStore.modalStore.showConfirm(
                'The SSN field is empty. Do you want to proceed anyway?',
                async () => {
                    await this.createPatient();
                }
            );
        }
        else {
            await this.createPatient();
        }
    }

    @action.bound
    async createPatient() {
        this.loaderStore.suspend();                
        this.form.setFieldsDisabled(true);
        try {
            await this.patientDetailService.import({ id: this.id });  
            if(this.rootStore.routerStore.routeHistory.length > 1){
                this.rootStore.routerStore.goBack();
            }else{
                this.rootStore.routerStore.goTo('master.app.main.import.detail.list', { id: this.item.importHeaderId });                                            
            }
            this.rootStore.notificationStore.success("Import successfully done.");                       
        }
        catch(err) {
            this.rootStore.notificationStore.error("Error occured on import.");
        }

        this.form.setFieldsDisabled(false);   
        this.loaderStore.resume(); 
    }

    @action.bound
    constructDiagnosisCodesDropdownsStore() {
        return new BaasicDropdownStore({
            textField: 'code',
            dataItemKey: 'diagnosisCodeId',
            filterable: true,
            multi: true,
            }, {                
            fetchFunc: async(term) => {
                const response = await this.diagnosisCodeService.find({ searchQuery: term, rpp: 20 /* rpp: 71500 */});

                let mappedResult = _.map(response.data.item, item => {
                    return _.extend(item, {diagnosisCodeId: item.id});
                });

                return mappedResult;
            }
        });
    }

    @action.bound
    constructInsuranceDropdownStore(prefix) {
        return new BaasicDropdownStore({
            textField: 'insuranceName',
            dataItemKey: 'insuranceName',
            filterable: true            
        }, {
        fetchFunc: async (term) => {
            const response = await this.insuranceService.find({ searchQuery: term });
            return response.data.item;
        },
        onChange: (insurance) => {
            if (insurance)
            {
                this.form.$(prefix + 'InsurancePolicyNumber').set('rules', 'required');
                this.form.$(prefix + 'InsuranceStartDate').set('rules', 'required');
                this.form.$(prefix + 'InsuranceStartDate').value = moment('1980-01-01', 'YYYY-MM-DD');                    
                if (prefix == 'oldPrimary')
                    this.form.$(prefix + 'InsuranceEndDate').set('rules', 'required');
            }
        }
        });
    }
    
    @action.bound
    async getResource(id) {
        await super.getResource(id);

        runInAction(() => {
            this.form.$('patientDetailDiagnosisCodes').set(this.item.patientDetailDiagnosisCodes);
            this.form.$('dateOfBirth').set(toDate(this.item.dateOfBirth));
            this.form.validate();
            this.oldPrimaryInsuranceDisplayed = !!this.item.oldPrimaryInsuranceName
        });
    }

    @action.bound
    async updateResource(resource) {
        if (!resource.ssn) {
            this.rootStore.modalStore.showConfirm(
                'The SSN field is empty. Do you want to proceed anyway?',
                async () => {
                    await this.updatePatientDetail(resource);
                }
            );
        }
        else {
            await this.updatePatientDetail(resource);
        }
    }

    @action.bound
    async updatePatientDetail(resource) {
        this.loaderStore.suspend();
        this.form.setFieldsDisabled(true);
        try {
            await this.actions.update({
                id: this.id,
                ...resource,
            });
            this.rootStore.notificationStore.success("Changes successfully saved.");
        }
        catch(err) {
            this.rootStore.notificationStore.error("Error occured while saving.");
        }
        this.form.setFieldsDisabled(false);
        this.loaderStore.resume();
    }

    @action.bound
    async onInit({ initialLoad }) {
        if(!initialLoad) {
            this.rootStore.routerStore.goBack();
        }
    }
}

export default ImportPatientCreateViewStore;
